import React, { useState } from 'react';
import { LuCamera, LuChevronRight, LuTriangleAlert } from 'react-icons/lu';
import { ubicacionLegible } from '../../../utils/pdf/porEquipo';
import RespuestaObservado from './RespuestaObservado';

// Equipos atendidos en el mes, para revisar antes de facturar (14-oct-2026). El renglón que la
// empresa no reconoció en su portal va primero, en rojo, con cómo responderle (15-oct-2026).
// Tocar un renglón abre ese trabajo.
const ddmm = (iso) => { const [, m, d] = String(iso || '').slice(0, 10).split('-'); return d ? `${d}/${m}` : ''; };
const VISIBLES = 8;

export default function EquiposDelMes({ filas = [], observados = [], respuestas = {}, onAbrir, onResponder, onCorregir }) {
    const [todos, setTodos] = useState(false);
    const obs = new Set(observados);
    const orden = [...filas].sort((a, b) => (obs.has(b.itemId) ? 1 : 0) - (obs.has(a.itemId) ? 1 : 0));
    const lista = todos ? orden : orden.slice(0, VISIBLES);
    // Respondidos que ya no están en el mes (sacados, o corregidos: el trabajo cambió): quedan anotados abajo
    const quitados = Object.entries(respuestas).filter(([id]) => !filas.some(f => String(f.itemId) === id));
    if (!filas.length && !quitados.length) return <p className="px-1 text-caption text-muted">Todavía no hay equipos atendidos este mes.</p>;

    return (
        <div>
            <div className="rounded-xl bg-panel divide-y divide-black/[0.05] dark:divide-white/[0.05]">
                {lista.map((f, i) => {
                    const marcado = obs.has(f.itemId);
                    const resp = respuestas[String(f.itemId)];
                    // 1-nov-2026: el cliente como en Trello ("2995 · Martín"). "Equipo 1" es un nombre genérico que
                    // puso la importación de Trello: no dice nada, se usa el cliente.
                    const ubicPropia = /^equipo \d+$/i.test(String(f.ubicacion || '').trim()) ? null : ubicacionLegible({ ubicacionEquipo: f.ubicacion });
                    const quien = [f.codigo, f.sede].filter(Boolean).join(' · ');
                    const ubic = [quien || f.direccion, ubicPropia].filter(Boolean).join(' · ');
                    return (
                        <div key={f.itemId || i} className={`px-3 py-2.5 ${marcado && !resp ? 'bg-[rgba(201,52,31,0.08)]' : ''}`}>
                            <button type="button" onClick={onAbrir ? () => onAbrir(f.servicioId) : undefined}
                                className="w-full flex items-center gap-2.5 text-left">
                                <span className="shrink-0 w-10 text-caption font-black text-muted">{ddmm(f.fecha)}</span>
                                <span className="flex-1 min-w-0">
                                    <span className="flex items-center gap-1.5 text-caption font-black text-ink">
                                        {marcado && <LuTriangleAlert size={13} className={`shrink-0 ${resp ? 'text-muted' : 'text-brand-red'}`} />}
                                        <span className="truncate">{f.serie ? `N/S ${f.serie}` : 'Sin N/S'}{ubic ? ` · ${ubic}` : ''}</span>
                                    </span>
                                    <span className={`block text-label truncate ${marcado && !resp ? 'text-brand-red font-bold' : 'text-muted'}`}>
                                        {marcado && !resp ? 'La empresa no lo reconoce' : (f.trabajo || 'Sin detalle')}
                                    </span>
                                    {/* 28-oct-2026: revisita por un reclamo; sin cargo no suma equipos */}
                                    {f.reclamo && (
                                        <span className={`mt-0.5 inline-flex h-5 px-1.5 rounded-md text-label font-black items-center ${f.sinCargo ? 'bg-[rgba(22,163,74,0.14)] text-[var(--etapa-listo-tx)]' : 'bg-[rgba(217,119,6,0.15)] text-[#B45309] dark:text-[#F0A500]'}`}>
                                            Reclamo{f.reclamo.fechaOriginal ? ` del ${ddmm(f.reclamo.fechaOriginal)}` : ''} · {f.sinCargo ? 'sin cargo, no suma' : 'se cobra'}
                                        </span>
                                    )}
                                </span>
                                <span className={`shrink-0 inline-flex items-center gap-1 text-label font-bold ${f.fotos >= 2 ? 'text-muted' : 'text-brand-amber'}`}
                                    title={f.fotos >= 2 ? 'Fotos cargadas' : 'Faltan fotos'}>
                                    <LuCamera size={12} /> {f.fotos || 0}
                                </span>
                                {onAbrir && <LuChevronRight size={15} className="shrink-0 text-muted" />}
                            </button>
                            {marcado && onResponder && (
                                <RespuestaObservado respuesta={resp}
                                    onResponder={(tipo, texto) => onResponder(f.itemId, tipo, texto)}
                                    onCorregir={onCorregir ? () => onCorregir(f.servicioId) : null} />
                            )}
                        </div>
                    );
                })}
            </div>
            {filas.length > VISIBLES && (
                <button type="button" onClick={() => setTodos(v => !v)} className="mt-1.5 px-1 text-caption font-bold text-secondary underline">
                    {todos ? 'Ver menos' : `Ver los ${filas.length} equipos`}
                </button>
            )}
            {quitados.length > 0 && (
                <div className="mt-2 px-3 py-2 rounded-xl bg-panel text-label text-muted space-y-0.5">
                    <p className="font-black uppercase tracking-widest">Ya respondidos</p>
                    {quitados.map(([id, r]) => (
                        <p key={id}><b className="text-secondary">{r.tipo === 'QUITADO' ? 'Sacado' : 'Corregido'}:</b> {r.serie ? `N/S ${r.serie}` : 'Equipo'}{r.fechaTrabajo ? ` · ${ddmm(r.fechaTrabajo)}` : ''}{r.texto ? ` — ${r.texto}` : ''}</p>
                    ))}
                </div>
            )}
        </div>
    );
}
